// SAB-426: conversion-optimized marketing homepage. Replaces LandingPage at "/"
// behind VITE_HOMEPAGE_VARIANT (unset = LandingPage, so rollback is one env var).
// Pricing stays pulled pending counsel. PromoBar restored (SAB-555 welcome-card offer).
// TrustLegality and FAQ are each gated behind their own VITE_SHOW_* flag.
import { Nav } from '@/components/marketing/Nav';
import { PromoBar } from '@/components/marketing/PromoBar';
import { Hero } from '@/components/marketing/Hero';
import { HowItWorks } from '@/components/marketing/HowItWorks';
import { SabotageCardsShowcase } from '@/components/marketing/SabotageCardsShowcase';
import { TrustLegality } from '@/components/marketing/TrustLegality';
import { FAQ } from '@/components/marketing/FAQ';
import { FinalCTA } from '@/components/marketing/FinalCTA';
import { MarketingFooter } from '@/components/marketing/MarketingFooter';
import { MobileStickyCta } from '@/components/marketing/MobileStickyCta';
import { Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '@/contexts/AuthContext';
import { AUTH_KEY } from '@/integrations/supabase/client';
import { DEFAULT_AUTHED_HOME } from '@/lib/landingAuthCopy';
import sabotagelogo from '@/assets/sabotage-logo.png';

// SAB-654: a synchronous "a session MIGHT exist" signal, used to decide whether to hold a
// splash while auth resolves — WITHOUT flashing that splash at a normal marketing visitor.
// authLoading is true on first paint and settles async, so gating on authLoading alone would
// render splash -> marketing for the unauthenticated. On WEB the session token is mirrored to
// localStorage (client.ts setItem), so its ABSENCE means definitely-unauthenticated and we go
// straight to marketing (no splash). On NATIVE the WKWebView can evict localStorage on cold
// boot (the SAB-561 reason), so the check is unreliable there — we fall back to the authLoading
// splash, which is the expected native cold-open experience and where opens are typically authed.
function maybeAuthenticated(): boolean {
  if (Capacitor.isNativePlatform()) return true;
  try {
    return !!localStorage.getItem(AUTH_KEY);
  } catch {
    return false;
  }
}

export default function MarketingHome() {
  // SAB-654: authenticated visitors must never see the marketing page — route them through the
  // /home landing gate (LandingRouter resolves their league). LandingPage already did this; the
  // prod '/' variant (MarketingHome) was missing it, so authed users landed on marketing on app
  // open. The decision lives at /home, so we only redirect here — no league logic duplicated.
  const { user, isLoading: authLoading } = useAuth();

  // Hold a branded splash ONLY when a session might exist. A token-less web visitor never sees
  // it and gets marketing on first paint (no flash).
  if (maybeAuthenticated() && authLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-14 w-auto animate-pulse" />
      </div>
    );
  }
  if (user) return <Navigate to={DEFAULT_AUTHED_HOME} replace />;

  return (
    <div className="min-h-screen bg-background text-foreground dark">
      <header className="sticky top-0 z-50">
        {/* SAB-555: PromoBar gated behind its own flag pending counsel sign-off on the
            welcome-offer rules. Unset (prod) hides the bar (Vite DCEs it); Preview sets
            it 'true' so the bar stays reviewable. MarketingHome, /promo-rules, and the
            server-side grant mechanic remain live regardless of this flag. */}
        {import.meta.env.VITE_SHOW_PROMOBAR === 'true' && <PromoBar />}
        <Nav />
      </header>
      {/* SAB-447: pb-20 sm:pb-0 reserves space for the mobile sticky CTA bar
          so the FinalCTA / footer aren't visually obscured at the very bottom.
          Desktop has no sticky bar so no padding needed. */}
      <main className="pb-20 sm:pb-0">
        <Hero />
        {/* SAB-578: the "Trash Scoring: Live" card moved into HowItWorks (side by
            side with the Week 1 example line). Its former standalone section here
            is removed. */}
        <HowItWorks />
        <SabotageCardsShowcase />
        {/* SAB-578: the "A normal Sunday" SocialProof section was removed (its real
            game anchor moved to HowItWorks; the remaining pushes were explicitly
            fictional with stale numbers). */}
        {/* TrustLegality gated behind its own flag until counsel confirms the
            legality sentence and the phased state footprint. Same pattern as
            VITE_SHOW_FAQ; Vite dead-code-eliminates it when unset. Nav.tsx gates
            the matching #trust anchor on the same flag. */}
        {import.meta.env.VITE_SHOW_TRUST_LEGALITY === 'true' && <TrustLegality />}
        {/* FAQ gated behind its own flag until counsel approves the answers.
            Unset (or anything other than 'true') hides the section and lets Vite
            dead-code-eliminate it, so the FAQ does not block the rest of
            MarketingHome. Nav.tsx gates the matching #faq anchor on the same flag. */}
        {import.meta.env.VITE_SHOW_FAQ === 'true' && <FAQ />}
        <FinalCTA />
      </main>
      <MarketingFooter />
      <MobileStickyCta />
    </div>
  );
}
